import React from "react";
import { Pressable, StyleSheet, Switch, Text, View } from "react-native";
import {
  behavioralToPickerId,
  PICKER_CATEGORIES,
  pickerCategoryLabel,
} from "../lockbox/utils/categoryPickerCatalog.js";

/**
 * Reusable genre picker — pre-selects auto-detected category; optional disturbing-content block.
 *
 * @param {{
 *   detectedCategory?: import('../lockbox/domain/types.js').BehavioralCategory,
 *   pickerCategoryId: string,
 *   onPickerCategoryChange: (id: string) => void,
 *   disturbing?: boolean,
 *   onDisturbingChange?: (v: boolean) => void,
 *   showDisturbing?: boolean,
 * }} props
 */
export function CategoryPicker({
  detectedCategory = "unknown",
  pickerCategoryId,
  onPickerCategoryChange,
  disturbing = false,
  onDisturbingChange,
  showDisturbing = true,
}) {
  const detectedPickerId = behavioralToPickerId(detectedCategory);
  const autoLabel = pickerCategoryLabel(detectedPickerId);

  return (
    <View style={styles.wrap}>
      <Text style={styles.label}>Category</Text>
      <Text style={styles.hint}>
        Auto-detected: <Text style={styles.hintStrong}>{autoLabel}</Text> — tap to override
      </Text>
      <View style={styles.grid}>
        {PICKER_CATEGORIES.map((cat) => {
          const on = pickerCategoryId === cat.id;
          return (
            <Pressable
              key={cat.id}
              style={[styles.chip, on ? styles.chipOn : null]}
              onPress={() => onPickerCategoryChange(cat.id)}
            >
              <Text style={[styles.chipText, on ? styles.chipTextOn : null]}>{cat.label}</Text>
            </Pressable>
          );
        })}
      </View>

      {showDisturbing && onDisturbingChange ? (
        <View style={styles.disturbRow}>
          <View style={styles.disturbText}>
            <Text style={styles.disturbLabel}>Contains disturbing content</Text>
            <Text style={styles.disturbSub}>Blocks this app in all modes (manual layer)</Text>
          </View>
          <Switch
            value={disturbing}
            onValueChange={onDisturbingChange}
            trackColor={{ false: "#d1d5db", true: "#f87171" }}
            thumbColor="#fff"
          />
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { marginVertical: 8 },
  label: { fontSize: 12, fontWeight: "700", color: "#374151" },
  hint: { fontSize: 11, color: "#6b7280", marginTop: 4, marginBottom: 8 },
  hintStrong: { fontWeight: "700", color: "#4338ca" },
  grid: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: "#f3f4f6",
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  chipOn: { backgroundColor: "#4f46e5", borderColor: "#4f46e5" },
  chipText: { fontSize: 12, fontWeight: "600", color: "#374151" },
  chipTextOn: { color: "#fff" },
  disturbRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 14,
    padding: 12,
    backgroundColor: "#fef2f2",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#fecaca",
  },
  disturbText: { flex: 1, paddingRight: 8 },
  disturbLabel: { fontSize: 13, fontWeight: "700", color: "#991b1b" },
  disturbSub: { fontSize: 11, color: "#b91c1c", marginTop: 2 },
});
